import { HashRouter, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './hooks/useAuth';
import { AuthGate } from './components/AuthGate';
import { Header } from './components/Header';
import { Ticker } from './components/Ticker';
import { TabBar } from './components/TabBar';
import { Onboarding } from './components/Onboarding';
import { Skeleton } from './components/States';
import { Icon } from './components/Icon';
import { NavLink } from 'react-router-dom';
import { MapView } from './components/MapView';
import { ForumsPage } from './pages/ForumsPage';
import { ForumPage } from './pages/ForumPage';
import { PostPage } from './pages/PostPage';
import { MapPage } from './pages/MapPage';
import { BlogPage } from './pages/BlogPage';
import { ProfilePage } from './pages/ProfilePage';
import { CountriesPage } from './pages/CountriesPage';
import { CountryPage } from './pages/CountryPage';
import { LivePage } from './pages/LivePage';
import { ToolsPage } from './pages/ToolsPage';
import { CasesPage } from './pages/CasesPage';
import { VerifyPage } from './pages/VerifyPage';

function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const wide = pathname.startsWith('/countries');
  const nav = [
    ['/', 'blog', 'Brief'],
    ['/live', 'live', 'Live'],
    ['/map', 'map', 'Map'],
    ['/tools', 'tools', 'Tools'],
    ['/cases', 'cases', 'Cases'],
    ['/verify', 'check', 'Verify'],
    ['/countries', 'world', 'Wiki'],
    ['/forums', 'forums', 'Forums'],
    ['/profile', 'profile', 'Profile'],
  ] as const;
  return (
    <div className="app-shell">
      <Header />
      <Ticker />
      <div className={`workspace${wide ? ' workspace--wide' : ''}`}>
        <aside className="sidebar" data-testid="desktop-sidebar">
          <div className="sidebar__label">OPERATIONS</div>
          <nav aria-label="Desktop navigation">
            {nav.map(([to, icon, label]) => (
              <NavLink key={to} to={to} end={to === '/'}>
                <Icon name={icon} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="sidebar__label">INTEL CHANNELS</div>
          <NavLink className="channel" to="/forums/overview">
            <i className="channel__dot channel__dot--red" />
            Global overview <b>12</b>
          </NavLink>
          <NavLink className="channel" to="/forums/airspace">
            <i className="channel__dot" />
            Airspace <b>4</b>
          </NavLink>
          <NavLink className="channel" to="/forums/jamming">
            <i className="channel__dot channel__dot--amber" />
            Jamming & EW <b>7</b>
          </NavLink>
          <div className="sidebar__status">
            <span>
              <i /> SYSTEMS NOMINAL
            </span>
            <small>Last sync 18s ago</small>
          </div>
        </aside>
        <main className="app-content">{children}</main>
        {!wide && <aside className="right-rail">
          <section className="rail-panel">
            <header>
              <span>LIVE MAP</span>
              <NavLink to="/map">EXPAND ↗</NavLink>
            </header>
            <MapView compact />
          </section>
          <section className="rail-panel trending">
            <header>
              <span>TRENDING SIGNALS</span>
            </header>
            <ol>
              <li>
                <b>01</b>
                <span>
                  Baltic airspace activity<small>42 reports · +18%</small>
                </span>
              </li>
              <li>
                <b>02</b>
                <span>
                  Red Sea transit risk<small>28 reports · +11%</small>
                </span>
              </li>
              <li>
                <b>03</b>
                <span>
                  GNSS interference<small>19 reports · +7%</small>
                </span>
              </li>
            </ol>
          </section>
          <section className="rail-panel analysts">
            <header>
              <span>TOP ANALYSTS</span>
            </header>
            <p>
              <i>VA</i>
              <span>
                VANDAL<small>12 first reports</small>
              </span>
              <b>98</b>
            </p>
            <p>
              <i>ME</i>
              <span>
                MERIDIAN<small>8 first reports</small>
              </span>
              <b>91</b>
            </p>
          </section>
        </aside>}
      </div>
      <TabBar />
    </div>
  );
}
export default function App() {
  const { user, isGuest, loading, showOnboarding } = useAuth();
  if (loading)
    return (
      <main className="boot">
        <Skeleton />
      </main>
    );
  if (!user && !isGuest) return <AuthGate />;
  return (
    <HashRouter>
      {showOnboarding && <Onboarding />}
      <Routes>
        <Route
          path="/"
          element={
            <Shell>
              <BlogPage />
            </Shell>
          }
        />
        <Route
          path="/brief"
          element={
            <Shell>
              <BlogPage />
            </Shell>
          }
        />
        <Route
          path="/forums"
          element={
            <Shell>
              <ForumsPage />
            </Shell>
          }
        />
        <Route
          path="/map"
          element={
            <Shell>
              <MapPage />
            </Shell>
          }
        />
        <Route
          path="/live"
          element={
            <Shell>
              <LivePage />
            </Shell>
          }
        />
        <Route
          path="/tools"
          element={
            <Shell>
              <ToolsPage />
            </Shell>
          }
        />
        <Route
          path="/cases"
          element={
            <Shell>
              <CasesPage />
            </Shell>
          }
        />
        <Route
          path="/verify"
          element={
            <Shell>
              <VerifyPage />
            </Shell>
          }
        />
        <Route
          path="/countries"
          element={
            <Shell>
              <CountriesPage />
            </Shell>
          }
        />
        <Route
          path="/countries/:id"
          element={
            <Shell>
              <CountryPage />
            </Shell>
          }
        />
        <Route
          path="/blog"
          element={
            <Shell>
              <BlogPage />
            </Shell>
          }
        />
        <Route
          path="/profile"
          element={
            <Shell>
              <ProfilePage />
            </Shell>
          }
        />
        <Route path="/forums/:id" element={<ForumPage />} />
        <Route path="/posts/:id" element={<PostPage />} />
      </Routes>
    </HashRouter>
  );
}
